import { apiClient } from '../api/apiClient';

interface CreateUserRequest {
  email: string;
  password: string;
  full_name: string;
}

interface CreateUserResponse {
  id: number;
  email: string;
}

interface OnboardingPayload {
  basic: {
    first_name: string;
    middle_name?: string;
    last_name: string;
    date_of_birth: string;
    gender: 'male' | 'female' | 'other';
    height_cm?: number;
    weight_kg?: number;
    body_type?: 'ectomorph' | 'mesomorph' | 'endomorph';
    activity_level?: 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active' | 'super_active';
    timezone: string;
    timezone_id?: number;
    email: string;
    phone_number: string;
    country_code_id?: number;
  };
  conditions: {
    condition_names: string[];
    other_condition_text?: string;
    allergies: string[];
    other_allergy_text?: string;
  };
  lifestyle: {
    smokes: boolean;
    drinks_alcohol: boolean;
    exercises_regularly: boolean;
    exercise_type?: 'gym' | 'running' | 'yoga' | 'other';
    exercise_frequency_per_week?: number;
  };
  notifications: {
    timezone: string;
    window_start_local: string;
    window_end_local: string;
    email_enabled?: boolean;
    sms_enabled?: boolean;
    push_enabled?: boolean;
  };
  consents: Array<{
    consent_type: 'data_storage' | 'recommendations' | 'terms_privacy';
    consented: boolean;
    version?: string;
  }>;
}

class PatientService {
  /**
   * Create a new patient account via doctor portal
   * Uses the doctor-specific endpoint that sets created_via='doctor'
   * @param sendWelcomeEmail - If false, no verification/welcome email is sent; doctor can send later from patient row (e.g. after setting up nutrition profile and meal plan).
   */
  async createPatientAccount(
    email: string,
    password: string | null,
    onboardingData: OnboardingPayload,
    sendWelcomeEmail: boolean = true
  ): Promise<CreateUserResponse & { temporary_password?: string; message?: string }> {
    try {
      const response = await apiClient.post('/api/v1/doctors/patients/create', {
        email,
        password: password || null,
        onboarding_data: onboardingData,
        send_welcome_email: sendWelcomeEmail,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to create patient account:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use createPatientAccount instead
   * Create a new user account
   */
  async createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
    try {
      const response = await apiClient.post('/api/v1/auth/register', {
        email: data.email,
        password: data.password,
        full_name: data.full_name,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to create user:', error);
      throw error;
    }
  }

  /**
   * Submit onboarding data for a user
   * Note: For doctor-created patients, notifications and consents are set to defaults
   * Patient will update these when they log in to the app
   */
  async submitOnboarding(userId: number, payload: OnboardingPayload): Promise<void> {
    try {
      // Note: This endpoint requires the user to be authenticated
      // For doctor-created users, we may need a different endpoint or impersonation
      // For now, using the standard onboarding endpoint
      // The backend should handle doctor-created users appropriately
      await apiClient.post('/api/v1/onboarding/submit', payload);
    } catch (error: any) {
      console.error('Failed to submit onboarding:', error);
      throw error;
    }
  }

  /**
   * Create nutrition profile for a patient
   */
  async createNutritionProfile(userId: number, profileData: any): Promise<any> {
    try {
      const response = await apiClient.post('/api/v1/nutrition/profile', {
        user_id: userId,
        ...profileData,
        captured_via_enum: 'doctor_visit',
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to create nutrition profile:', error);
      throw error;
    }
  }
}

export const patientService = new PatientService();
